import { defineMessages } from 'react-intl';

import { phoneValidate } from '~/shared/utils/phoneValidate';
import { emailAddressIsValid } from '~/shared/utils/validateEmail';

export const errorMessages = defineMessages({
  email: {
    id: 'signup.error_email',
    defaultMessage: 'Invalid email address',
  },
  emailExists: {
    id: 'signup.error_email_exists',
    defaultMessage:
      'Looks like that email already has an account with Fabletics! Log in to that account to get access to both brands <a href="/login">here</a>.',
  },
  password: {
    id: 'signup.error_password_length',
    defaultMessage: 'Password must be at least 6 characters',
  },
  phone: {
    id: 'signup.error_phone_length',
    defaultMessage: 'Phone must be 10 digits',
  },
  required: {
    id: 'signup.error_required',
    defaultMessage: 'Required',
  },
  general: {
    id: 'signup.error_general_server',
    defaultMessage:
      'Something went wrong. We are working to fix it ASAP. Please try again!',
  },
});

/**
 * Validates form input values and returns an object containing error messages for invalid fields.
 *
 * @param {Object} params - The parameters for validation.
 * @param {Object} params.values - The form field values.
 * @param {string} params.values.email - The user's email address.
 * @param {string} params.values.password - The user's password (optional unless `requirePassword` is true).
 * @param {string} params.values.referrer - The referrer field.
 * @param {string} params.values.phone - The user's phone number (optional).
 * @param {boolean} params.requirePassword - Whether a password is required for validation.
 * @param {string} params.tld - The current top level domain
 *
 * @returns {Object} An object containing error messages for each invalid field.
 * The keys correspond to field names and the values are the error messages.
 *
 * @example
 * const errors = validateForm({
 *   values: {
 *     email: 'user@example.com',
 *     password: '12345',
 *     referrer: '',
 *     phone: '123-456-7890'
 *   },
 *   requirePassword: true,
 *   tld: "es"
 * });
 * // returns:
 * // {
 * //   password: 'Password must be at least 6 characters.',
 * //   referrer: 'This field is required.'
 * // }
 */
export const validateForm = ({ values, requirePassword, tld }) => {
  const errors = {};

  if (!values.email) {
    errors.email = errorMessages.required;
  } else if (!emailAddressIsValid(values.email)) {
    errors.email = errorMessages.email;
  }
  if (!values.referrer?.value) {
    errors.referrer = errorMessages.required;
  }
  if (requirePassword && !values.password) {
    errors.password = errorMessages.required;
  } else if (requirePassword && values.password.length < 6) {
    errors.password = errorMessages.password;
  }

  // dashes are part of values.phone
  const phone = values.phone;
  if (phone) {
    if (!phoneValidate(phone, { tld })) {
      errors.phone = errorMessages.phone;
    }
  }

  return errors;
};

/**
 * Returns a specific signup error message based on the error response.
 *
 * @param {Object} error - The error object returned from the server.
 * @param {number} error.statusCode - HTTP status code of the error.
 * @param {Array<{ code: number }>} [error.errorData] - Optional array of error details.
 * @returns {string} Corresponding error message string.
 */
export function getSignupError(error) {
  const { statusCode, errorData } = error;
  if (statusCode >= 500) {
    return errorMessages.general;
  }
  if (statusCode >= 400) {
    if (errorData) {
      const { code } = errorData[0];
      if (code === -32606) {
        return errorMessages.emailExists;
      }
    }
  }
  return errorMessages.general;
}
